import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  Link as LinkIcon,
  LinkOff as LinkOffIcon,
  OpenInNew as OpenInNewIcon,
  Refresh as RefreshIcon,
  Warning as WarningIcon,
} from '@mui/icons-material';
import api from '../../utils/api';
import { skipWhenOffline, OfflineNotice, useIsOffline } from '../../offline';
import { INSTANT_FORMATS } from '../../utils/dateUtils';
import { useFormatInstant } from '../../hooks/useClinicTimezone';

interface QuickbooksStatus {
  connected: boolean;
  realmId: string | null;
  connectedAt: string | null;
  expiresAt: string | null;
  status: 'connected' | 'expired' | 'revoked' | null;
  canManage: boolean;
  configured: boolean;
  qboHomeUrl: string;
}

interface SyncLogSummary {
  totalThisMonth: number;
  failedCount: number;
}

interface SyncLogEntry {
  id: string;
  entityType: 'invoice' | 'payment' | 'client';
  entityId: string;
  action: 'create' | 'update' | 'void';
  status: 'success' | 'failed';
  qbEntityId: string | null;
  error: string | null;
  attempts: number;
  createdAt: string;
}

interface Props {
  onSuccess: (message: string) => void;
  onError: (message: string) => void;
}

const QuickbooksConnectCard: React.FC<Props> = ({ onSuccess, onError }) => {
  const isOffline = useIsOffline();
  // Every date on this card is an INSTANT rendered in the CLINIC zone (DATE-KINDS §1
  // "QuickBooks"): `connectedAt`/`expiresAt` are Clinic.qboConnectedAt/qboTokenExpiresAt
  // (OAuth stamps), and a sync-log row's createdAt is `@default(now())`.
  const formatInstant = useFormatInstant();
  const formatDate = (iso: string | null) => formatInstant(iso, INSTANT_FORMATS.date) || '—';
  const [status, setStatus] = useState<QuickbooksStatus | null>(null);
  // Distinct from the fabricated not-configured fallback: offline we simply
  // don't know, and must not claim "not yet available" to a connected clinic.
  const [statusUnavailable, setStatusUnavailable] = useState(false);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [disconnecting, setDisconnecting] = useState(false);
  const [confirmDisconnect, setConfirmDisconnect] = useState(false);
  const [syncLog, setSyncLog] = useState<SyncLogEntry[]>([]);
  const [syncSummary, setSyncSummary] = useState<SyncLogSummary | null>(null);
  const [syncLogLoading, setSyncLogLoading] = useState(false);
  const [showAllActivity, setShowAllActivity] = useState(false);
  const [retrying, setRetrying] = useState<string | null>(null);

  const fetchStatus = useCallback(async () => {
    // Offline: skip the fetch — a failed call would fabricate {configured:false}
    // and render "not yet available, contact support" to a connected clinic.
    if (!skipWhenOffline()) {
      setStatus(null);
      setStatusUnavailable(true);
      setLoading(false);
      return;
    }
    try {
      setLoading(true);
      setStatusUnavailable(false);
      const res = await api.get('/quickbooks/status');
      setStatus(res.data);
    } catch (err) {
      console.error('Failed to fetch QuickBooks status', err);
      setStatus({
        connected: false,
        realmId: null,
        connectedAt: null,
        expiresAt: null,
        status: null,
        canManage: false,
        configured: false,
        qboHomeUrl: 'https://app.qbo.intuit.com/app/homepage',
      });
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchSyncLog = useCallback(async () => {
    if (!skipWhenOffline()) return; // keep the last-loaded log instead of a doomed fetch
    try {
      setSyncLogLoading(true);
      const params = showAllActivity ? '' : '?status=failed';
      const res = await api.get(`/quickbooks/sync-log${params}`);
      setSyncLog(res.data.entries || []);
      setSyncSummary(res.data.summary || null);
    } catch (err) {
      console.error('Failed to fetch sync log', err);
    } finally {
      setSyncLogLoading(false);
    }
  }, [showAllActivity]);

  useEffect(() => {
    fetchStatus();
  }, [fetchStatus]);

  // Reconnect: replace the offline "status unknown" placeholder with real data.
  useEffect(() => {
    if (!isOffline && statusUnavailable) fetchStatus();
  }, [isOffline, statusUnavailable, fetchStatus]);

  useEffect(() => {
    if (status?.connected) fetchSyncLog();
  }, [status?.connected, fetchSyncLog]);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const success = params.get('qb_success');
    const error = params.get('qb_error');
    if (success === 'true') {
      onSuccess('Successfully connected QuickBooks.');
      window.history.replaceState({}, '', window.location.pathname + '?tab=integrations');
      fetchStatus();
    } else if (error) {
      // Defense in depth: backend already strips URLs + caps length, but the
      // value still rides through window.location.search where an attacker
      // could craft `?qb_error=...` directly. Strip anything that looks like
      // a URL or callout text, and cap to a sane length before surfacing.
      const raw = decodeURIComponent(error);
      const sanitized = raw
        .replace(/https?:\/\/\S+/gi, '')
        .replace(/[<>]/g, '')
        .trim()
        .slice(0, 200);
      onError(sanitized || 'QuickBooks connection failed.');
      window.history.replaceState({}, '', window.location.pathname + '?tab=integrations');
    }
  }, [onSuccess, onError, fetchStatus]);

  const handleConnect = async () => {
    try {
      setActionLoading(true);
      const res = await api.post('/quickbooks/connect');
      const { authUrl } = res.data;
      if (authUrl) {
        window.location.href = authUrl;
      } else {
        throw new Error('No auth URL returned');
      }
    } catch (err: any) {
      console.error('Failed to start QuickBooks connect', err);
      onError(err.response?.data?.message || 'Failed to connect to QuickBooks.');
      setActionLoading(false);
    }
  };

  /**
   * Reconnect = disconnect + connect in one click. Used when the refresh token
   * has expired (100-day idle) or been revoked from QBO — the clinic admin
   * shouldn't have to hunt for two buttons.
   */
  const handleReconnect = async () => {
    try {
      setActionLoading(true);
      // Best-effort disconnect; ignore errors since the connection may already
      // be in a broken state (that's why we're reconnecting).
      try {
        await api.post('/quickbooks/disconnect');
      } catch (disconnectErr) {
        console.warn('Pre-reconnect disconnect failed (continuing):', disconnectErr);
      }
      const res = await api.post('/quickbooks/connect');
      const { authUrl } = res.data;
      if (authUrl) {
        window.location.href = authUrl;
      } else {
        throw new Error('No auth URL returned');
      }
    } catch (err: any) {
      console.error('Failed to reconnect QuickBooks', err);
      onError(err.response?.data?.message || 'Failed to reconnect to QuickBooks.');
      setActionLoading(false);
    }
  };

  const handleRetry = async (logId: string) => {
    try {
      setRetrying(logId);
      await api.post(`/quickbooks/sync-log/${logId}/retry`);
      onSuccess('Sync retried successfully.');
      await fetchSyncLog();
    } catch (err: any) {
      console.error('Failed to retry sync', err);
      onError(err.response?.data?.message || 'Retry failed.');
    } finally {
      setRetrying(null);
    }
  };

  const handleDisconnect = async () => {
    try {
      setDisconnecting(true);
      await api.post('/quickbooks/disconnect');
      setConfirmDisconnect(false);
      onSuccess('QuickBooks disconnected.');
      await fetchStatus();
    } catch (err: any) {
      console.error('Failed to disconnect QuickBooks', err);
      onError(err.response?.data?.message || 'Failed to disconnect QuickBooks.');
    } finally {
      setDisconnecting(false);
    }
  };

  const statusChip = () => {
    if (statusUnavailable) {
      return <Chip label="Status unavailable offline" size="small" variant="outlined" />;
    }
    if (!status?.connected) return <Chip label="Not Connected" size="small" />;
    if (status.status === 'expired') {
      return <Chip label="Expired" color="warning" size="small" icon={<WarningIcon />} />;
    }
    if (status.status === 'revoked') {
      return <Chip label="Revoked" color="error" size="small" icon={<WarningIcon />} />;
    }
    return <Chip label="Connected" color="success" size="small" icon={<CheckCircleIcon />} />;
  };

  return (
    <Card variant="outlined">
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
          <Typography variant="h6" sx={{ mr: 2 }}>
            QuickBooks Online
          </Typography>
          {!loading && statusChip()}
        </Box>
        <Typography variant="body2" color="text.secondary">
          Push StableTrack invoices, customers, and payments into your QuickBooks Online account —
          no more duplicate entry at month-end.
        </Typography>

        <OfflineNotice sx={{ mt: 2, mb: 0 }}>
          QuickBooks status is unavailable offline — reconnect to manage accounting sync.
        </OfflineNotice>

        {loading ? (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, py: 2 }}>
            <CircularProgress size={24} />
            <Typography color="text.secondary">Loading QuickBooks status...</Typography>
          </Box>
        ) : statusUnavailable ? null /* OfflineNotice above explains it */ : !status?.configured ? (
          <Alert severity="info" sx={{ mt: 2 }}>
            QuickBooks integration is not yet available. Contact support if you&apos;d like early
            access.
          </Alert>
        ) : !status.connected ? (
          <Box sx={{ mt: 2 }}>
            <Button
              variant="contained"
              onClick={handleConnect}
              disabled={actionLoading || !status.canManage || isOffline}
            >
              {actionLoading ? <CircularProgress size={20} sx={{ mr: 1 }} /> : null}
              Connect QuickBooks
            </Button>
            {!status.canManage && (
              <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                Only clinic owners and admins can manage QuickBooks.
              </Typography>
            )}
          </Box>
        ) : (
          <Box sx={{ mt: 2 }}>
            <Stack direction="row" spacing={4} sx={{ mb: 2 }}>
              <Box>
                <Typography variant="caption" color="text.secondary">
                  Company ID
                </Typography>
                <Typography variant="body2">{status.realmId || '—'}</Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary">
                  Connected
                </Typography>
                <Typography variant="body2" data-testid="qbo-connected-at">
                  {formatDate(status.connectedAt)}
                </Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary">
                  Token expires
                </Typography>
                <Typography variant="body2" data-testid="qbo-token-expires-at">
                  {formatDate(status.expiresAt)}
                </Typography>
              </Box>
            </Stack>
            {(status.status === 'expired' || status.status === 'revoked') && (
              <Alert
                severity={status.status === 'revoked' ? 'error' : 'warning'}
                sx={{ mb: 2 }}
                action={
                  status.canManage ? (
                    <Button
                      color="inherit"
                      size="small"
                      onClick={handleReconnect}
                      disabled={actionLoading || isOffline}
                      startIcon={<LinkIcon />}
                    >
                      Reconnect
                    </Button>
                  ) : undefined
                }
              >
                {status.status === 'revoked'
                  ? 'Your QuickBooks connection was revoked (likely disconnected from inside QuickBooks). Reconnect to resume syncing.'
                  : 'Your QuickBooks connection has expired. Reconnect to resume syncing.'}
              </Alert>
            )}
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
              <Button
                variant="outlined"
                startIcon={<OpenInNewIcon />}
                href={status.qboHomeUrl}
                target="_blank"
                rel="noopener noreferrer"
              >
                Open QuickBooks
              </Button>
              {status.canManage && (status.status === 'expired' || status.status === 'revoked') && (
                <Button
                  variant="contained"
                  color="primary"
                  startIcon={<LinkIcon />}
                  onClick={handleReconnect}
                  disabled={actionLoading || isOffline}
                >
                  {actionLoading ? <CircularProgress size={20} sx={{ mr: 1 }} /> : null}
                  Reconnect QuickBooks
                </Button>
              )}
              {status.canManage && (
                <Button
                  variant="outlined"
                  color="error"
                  startIcon={<LinkOffIcon />}
                  onClick={() => setConfirmDisconnect(true)}
                  disabled={isOffline}
                >
                  Disconnect
                </Button>
              )}
            </Stack>

            <Divider sx={{ my: 3 }} />

            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
              <Typography variant="subtitle2">Sync activity</Typography>
              <Tooltip title="Refresh">
                <IconButton size="small" onClick={fetchSyncLog} disabled={syncLogLoading || isOffline}>
                  <RefreshIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>

            {syncSummary && (
              <Alert
                severity={syncSummary.failedCount > 0 ? 'warning' : 'success'}
                sx={{ mb: 2 }}
                action={
                  syncSummary.failedCount === 0 ? undefined : undefined
                }
              >
                {syncSummary.failedCount === 0
                  ? `All ${syncSummary.totalThisMonth} syncs this month succeeded.`
                  : `${syncSummary.failedCount} sync${syncSummary.failedCount === 1 ? '' : 's'} failed — review below. ${syncSummary.totalThisMonth} succeeded this month.`}
              </Alert>
            )}

            {syncLogLoading ? (
              <CircularProgress size={20} />
            ) : syncLog.length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                {showAllActivity
                  ? 'No sync activity yet. Invoices and payments will appear here after they\u2019re pushed to QuickBooks.'
                  : 'No failures. Click "Show all activity" to see the full sync history.'}
              </Typography>
            ) : (
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>When</TableCell>
                    <TableCell>Entity</TableCell>
                    <TableCell>Action</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell align="right">&nbsp;</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {syncLog.map((entry) => (
                    <TableRow key={entry.id} hover>
                      <TableCell>
                        <Tooltip title={formatInstant(entry.createdAt, INSTANT_FORMATS.dateTimeNumeric)}>
                          <span data-testid="qbo-sync-log-when">
                            {formatInstant(entry.createdAt, INSTANT_FORMATS.dateNumeric)}
                          </span>
                        </Tooltip>
                      </TableCell>
                      <TableCell>{entry.entityType}</TableCell>
                      <TableCell>{entry.action}</TableCell>
                      <TableCell>
                        {entry.status === 'success' ? (
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <CheckCircleIcon color="success" fontSize="small" />
                            <Typography variant="body2">Success</Typography>
                          </Box>
                        ) : (
                          <Tooltip title={entry.error || 'Unknown error'}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                              <ErrorIcon color="error" fontSize="small" />
                              <Typography variant="body2">Failed</Typography>
                            </Box>
                          </Tooltip>
                        )}
                      </TableCell>
                      <TableCell align="right">
                        {entry.status === 'failed' && status.canManage && (
                          <Button
                            size="small"
                            onClick={() => handleRetry(entry.id)}
                            disabled={retrying === entry.id || isOffline}
                          >
                            {retrying === entry.id ? <CircularProgress size={16} /> : 'Retry'}
                          </Button>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}

            <Button
              size="small"
              sx={{ mt: 1 }}
              onClick={() => setShowAllActivity((v) => !v)}
            >
              {showAllActivity ? 'Show failures only' : 'Show all activity'}
            </Button>
          </Box>
        )}
      </CardContent>

      <Dialog open={confirmDisconnect} onClose={() => setConfirmDisconnect(false)}>
        <DialogTitle>Disconnect QuickBooks?</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure? No data will be deleted, but StableTrack will stop syncing new invoices
            and payments to QuickBooks until you reconnect.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDisconnect(false)}>Cancel</Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleDisconnect}
            disabled={disconnecting}
          >
            {disconnecting ? <CircularProgress size={20} sx={{ mr: 1 }} /> : null}
            Disconnect
          </Button>
        </DialogActions>
      </Dialog>
    </Card>
  );
};

export default QuickbooksConnectCard;
